import React, { useState, useEffect } from "react";
import axios from "axios";
import "./orders.css";
import { Link } from "react-router-dom";
import { io } from "socket.io-client";
const Orders = ({ logout, user, API_URL }) => {

    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [managerFilter, setManagerFilter] = useState("");
    const [tourFilter, setTourFilter] = useState("");
    const fetchOrders = async () => {
        try {
            const response = await axios.post(`${API_URL}/getRequests`);
            const data = Array.isArray(response.data) ? response.data : [];
            data.sort((a, b) => Number(b.orderNumber) - Number(a.orderNumber));
            setOrders(data);
        } catch (error) {
            console.error("Ошибка загрузки заказов:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders();
    }, [API_URL]);

    useEffect(() => {
        const socketUrl = API_URL.replace(/\/api\/?$/, "");
        const socket = io(socketUrl);

        socket.on("calendar:update", () => {
            fetchOrders();
        });

        return () => socket.disconnect();
    }, [API_URL]);

    // Поиск: № заказа, имя гостя, партнёр (компания)
    function matchesSearch(order) {
        const q = searchQuery.trim().toLowerCase().replace(/^№/, "");
        if (!q) return true;

        if (String(order.orderNumber ?? "").includes(q)) return true;
        if (order.title && order.title.toLowerCase().includes(q)) return true; // у партнёров title = компания
        if (Array.isArray(order.guests)) {
            return order.guests.some((g) => g.name && g.name.toLowerCase().includes(q));
        }
        return false;
    }

    // Списки для фильтров — из самих заказов, без повторов, по алфавиту
    const uniqSorted = (arr) => [...new Set(arr.filter(Boolean).map((s) => String(s).trim()))].sort((a, b) => a.localeCompare(b, "ru"));
    const managerOptions = uniqSorted(orders.map((o) => o.manager));
    const tourOptions = uniqSorted(orders.map((o) => o.tour));

    const filteredOrders = orders.filter((o) =>
        matchesSearch(o) &&
        (!managerFilter || (o.manager || "").trim() === managerFilter) &&
        (!tourFilter || (o.tour || "").trim() === tourFilter)
    );
    const hasFilters = searchQuery || managerFilter || tourFilter;

    // Деньги: 12345 → "12 345"
    function formatMoney(amount) {
        return Number(amount || 0).toLocaleString("ru-RU");
    }

    // Инициалы менеджера
    function getInitials(name) {
        if (!name) return "—";
        const parts = name.trim().split(/\s+/);
        if (parts.length > 1) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        return (name.trim()[0] + name.trim()[name.trim().length - 1]).toUpperCase();
    }

    // Статус/тип заказа → { label, cls }
    function getStatus(order) {
        if (order.type === "sharetour") return { label: "Групповой", cls: "share" };
        const payLabels = {
            cash: { label: "Наличка", cls: "paid" },
            terminal: { label: "Терминал", cls: "transfer" },
            transfer: { label: "Перечисление", cls: "transfer" },
            debt: { label: "Долг", cls: "debt" },
        };
        return payLabels[order.paymentType] || { label: "—", cls: "debt" };
    }

    // "YYYY-MM-DD" → "11 июн 2026"
    const RU_M = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
    function formatDate(dateStr) {
        if (!dateStr) return "—";
        const [y, m, d] = dateStr.split("-");
        return `${Number(d)} ${RU_M[Number(m) - 1]} ${y}`;
    }

    const totalSum = filteredOrders.reduce((sum, o) => sum + (Number(o.grandTotal) || 0), 0);


    return (
        <div className="orders">
            <div className="orders-flex">

                {/* Шапка */}
                <div className="orders-head">
                    <div className="orders-head__left">
                        <h2>Все заказы</h2>
                        <div className="orders-search">
                            <i className="fa-solid fa-magnifying-glass"></i>
                            <input
                                type="text"
                                placeholder="ФИО / Номер заказа"
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                            />
                            {searchQuery && (
                                <i
                                    className="fa-solid fa-xmark"
                                    onClick={() => setSearchQuery("")}
                                ></i>
                            )}
                        </div>
                        <select className="orders-filter" value={managerFilter} onChange={(e) => setManagerFilter(e.target.value)}>
                            <option value="">Все менеджеры</option>
                            {managerOptions.map((m) => <option key={m} value={m}>{m}</option>)}
                        </select>
                        <select className="orders-filter" value={tourFilter} onChange={(e) => setTourFilter(e.target.value)}>
                            <option value="">Все туры</option>
                            {tourOptions.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                        {hasFilters && (
                            <button
                                type="button"
                                className="orders-filter-reset"
                                onClick={() => { setSearchQuery(""); setManagerFilter(""); setTourFilter(""); }}
                            >
                                Сбросить
                            </button>
                        )}
                    </div>
                    <div className="orders-head__stats">
                        <span>{filteredOrders.length} заказов</span>
                        <span>·</span>
                        <span>{formatMoney(totalSum)}$ всего</span>
                    </div>
                </div>

                {/* Список */}
                <div className="orders-list">
                    {loading ? (
                        <div className="orders-empty">Загрузка...</div>
                    ) : filteredOrders.length === 0 ? (
                        <div className="orders-empty">
                            {hasFilters ? "Ничего не найдено" : "Заказов пока нет"}
                        </div>
                    ) : (
                        filteredOrders.map(order => {
                            const status = getStatus(order);
                            return (
                                <div key={`${order.type}-${order.id}`} className={`orow orow--${status.cls}`}>
                                    <div className="orow__num">№{order.orderNumber}</div>

                                    <div className="orow__main">
                                        <div className="orow__title">{order.title}</div>
                                        <div className="orow__tour">{order.tour} · {order.participantsCount} чел.</div>
                                    </div>

                                    <div className="orow__date">
                                        {formatDate(order.dateStart)}
                                        {order.dateEnd && order.dateEnd !== order.dateStart ? ` – ${formatDate(order.dateEnd)}` : ""}
                                    </div>

                                    <span className="orow__pill">{status.label}</span>

                                    <div className="orow__manager">
                                        <span className="orow__avatar">{getInitials(order.manager)}</span>
                                        <span>{order.manager}</span>
                                    </div>

                                    <div className="orow__sum">{formatMoney(order.grandTotal)}$</div>

                                    <Link className="orow__more" to={`/tourdetails/${order.orderNumber}`}>
                                        Подробнее <i className="fa-solid fa-arrow-right-long"></i>
                                    </Link>

                                </div>
                            );
                        })
                    )}
                </div>

            </div>
        </div>
    );
};

export default Orders;